/**
 * Canonical activation-readiness helpers (activation-specific).
 *
 * These map the backend activation-readiness snapshot (the single source of
 * truth returned by GET /employees/{id}/activation-readiness and the bulk
 * unified-progress-summary) into consistent UI labels, tones and actions.
 *
 * This file is deliberately activation-scoped and does NOT touch the shared
 * status microcopy/label helpers owned by the parallel rejection/acceptance
 * branch.
 */

export const ACTIVATION_STATES = {
  ACTIVE: 'ACTIVE',
  STATUS_UNAVAILABLE: 'STATUS_UNAVAILABLE',
  NOT_IN_PIPELINE: 'NOT_IN_PIPELINE',
  ONBOARDING_IN_PROGRESS: 'ONBOARDING_IN_PROGRESS',
  DECISION_REQUIRED: 'READINESS_COMPLETE_DECISION_REQUIRED',
  READY_TO_ACTIVATE: 'READY_TO_ACTIVATE',
  FIT_FOR_WORK_BLOCKED: 'FIT_FOR_WORK_BLOCKED',
};

const PRESENTATION = {
  [ACTIVATION_STATES.ACTIVE]: {
    label: 'Active',
    tone: 'green',
    message: 'Active staff member.',
  },
  [ACTIVATION_STATES.STATUS_UNAVAILABLE]: {
    label: 'Activation status unavailable',
    tone: 'grey',
    message: 'We could not confirm activation status right now. Please retry shortly.',
  },
  [ACTIVATION_STATES.NOT_IN_PIPELINE]: {
    label: 'Not in onboarding',
    tone: 'grey',
    message: 'Recruitment approval and onboarding must be completed before activation.',
  },
  [ACTIVATION_STATES.ONBOARDING_IN_PROGRESS]: {
    label: 'Onboarding in progress',
    tone: 'amber',
    message: 'Outstanding onboarding requirements remain.',
  },
  [ACTIVATION_STATES.DECISION_REQUIRED]: {
    label: 'Readiness checks complete — final Fit-for-Work decision required',
    tone: 'blue',
    message: 'All readiness checks pass. A manager must record the final Fit-for-Work decision before activation.',
  },
  [ACTIVATION_STATES.READY_TO_ACTIVATE]: {
    label: 'Ready to activate',
    tone: 'green',
    message: 'All activation gates pass. This person can be promoted to active staff.',
  },
  [ACTIVATION_STATES.FIT_FOR_WORK_BLOCKED]: {
    label: 'Not ready for activation',
    tone: 'red',
    message: 'The latest Fit-for-Work decision blocks activation.',
  },
};

/**
 * Presentation for an activation snapshot. Always fails closed: any unknown or
 * missing snapshot is treated as "status unavailable" (never a percentage guess).
 */
export function getActivationPresentation(snapshot) {
  const rawState = snapshot && snapshot.activation_state;
  const isKnownState = Boolean(rawState && PRESENTATION[rawState]);
  const state = isKnownState ? rawState : ACTIVATION_STATES.STATUS_UNAVAILABLE;
  const preset = PRESENTATION[state];
  // Fail closed: only the one explicit READY_TO_ACTIVATE state may expose the CTA.
  // A new/unknown backend state must never inherit can_activate=true accidentally.
  const canActivate = Boolean(
    state === ACTIVATION_STATES.READY_TO_ACTIVATE
    && snapshot
    && snapshot.can_activate === true
  );
  const nextAction = isKnownState ? ((snapshot && snapshot.next_action) || null) : null;
  return {
    state,
    label: isKnownState && snapshot?.state_label ? snapshot.state_label : preset.label,
    tone: preset.tone,
    message: preset.message,
    canActivate,
    nextAction,
    // Convenience booleans for common UI branches.
    isActive: state === ACTIVATION_STATES.ACTIVE,
    isUnavailable: state === ACTIVATION_STATES.STATUS_UNAVAILABLE,
    needsFitForWork: state === ACTIVATION_STATES.DECISION_REQUIRED,
    isBlocked: state === ACTIVATION_STATES.FIT_FOR_WORK_BLOCKED,
    fitForWork: (snapshot && snapshot.fit_for_work) || null,
  };
}

/** True only when the canonical snapshot permits promotion right now. */
export function canActivateFromSnapshot(snapshot) {
  return Boolean(
    snapshot
    && snapshot.activation_state === ACTIVATION_STATES.READY_TO_ACTIVATE
    && snapshot.can_activate === true
  );
}
